<script setup>
import { ref, computed } from 'vue'
import { dayKey, monthGrid, weekDays } from '@/Utils/siteSchedule'

// Month / week grid of a site's calendar events (local zone, Monday first).
// Chips are draggable onto another day (HTML5 drag & drop → `move-event`),
// a click on a day's empty area asks to create an entry there (`select-day`),
// a click on a chip opens it (`select-event`).
const props = defineProps({
	events: {
		type: Array,
		default: () => [],
	},
	/** month | week */
	view: {
		type: String,
		default: 'month',
	},
	/** Any date inside the month / week to show. */
	cursor: {
		type: Date,
		required: true,
	},
	/** Content items by id — for poster / hidden state. */
	contentById: {
		type: Object,
		default: () => ({}),
	},
	/** id of the event being saved after a drop (rendered translucent) */
	movingId: {
		type: [Number, String],
		default: null,
	},
	maxPerDay: {
		type: Number,
		default: 3,
	},
})
const emit = defineEmits(['select-event', 'select-day', 'move-event'])

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const todayKey = computed(() => dayKey(new Date()))
const days = computed(() => (props.view === 'week' ? weekDays(props.cursor) : monthGrid(props.cursor.getFullYear(), props.cursor.getMonth())))
const weeks = computed(() => {
	const rows = []
	for (let i = 0; i < days.value.length; i += 7) rows.push(days.value.slice(i, i + 7))
	return rows
})

const eventsByDay = computed(() => {
	const map = {}
	for (const e of props.events) {
		const key = dayKey(e.start)
		if (!key) continue
		;(map[key] ||= []).push(e)
	}
	for (const list of Object.values(map)) list.sort((a, b) => new Date(a.start) - new Date(b.start))
	return map
})
const expanded = ref({})
const visibleEvents = (key) => {
	const list = eventsByDay.value[key] || []
	if (props.view === 'week' || expanded.value[key] || list.length <= props.maxPerDay) return list
	return list.slice(0, props.maxPerDay - 1)
}
const hiddenCount = (key) => (eventsByDay.value[key] || []).length - visibleEvents(key).length

const stateOf = (event, now = Date.now()) => {
	const start = new Date(event.start).getTime()
	const end = event.end ? new Date(event.end).getTime() : start
	if (now < start) return 'upcoming'
	return now <= end ? 'live' : 'ended'
}
const chipClass = (event) => {
	const item = props.contentById[String(event.contentId)]
	const state = stateOf(event)
	const base = 'group/chip flex w-full min-w-0 cursor-grab items-center gap-1.5 rounded px-1.5 py-0.5 text-left text-[11px] leading-4 transition-colors active:cursor-grabbing'
	const tone = state === 'live'
		? 'bg-success-transparent text-success hover:brightness-110'
		: state === 'ended'
			? 'bg-surface-4/70 text-surface-8 hover:bg-surface-4'
			: 'bg-main-transparent text-main-light hover:brightness-110'
	const hidden = item && item.published === false ? 'opacity-60' : ''
	const moving = String(props.movingId) === String(event.id) ? 'opacity-40' : ''
	return `${base} ${tone} ${hidden} ${moving}`
}
const chipTitle = (event) => event.title || event.content?.title || props.contentById[String(event.contentId)]?.title || `Content #${event.contentId}`
const fmtTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

// --- drag & drop -------------------------------------------------------------------
const dragId = ref(null)
const overKey = ref(null)
const onDragStart = (event, e) => {
	dragId.value = event.id
	if (e.dataTransfer) {
		e.dataTransfer.effectAllowed = 'move'
		e.dataTransfer.setData('text/plain', String(event.id))
	}
}
const onDragEnd = () => {
	dragId.value = null
	overKey.value = null
}
const onDragOver = (key, e) => {
	if (dragId.value === null) return
	e.preventDefault()
	if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
	overKey.value = key
}
const onDrop = (key, e) => {
	e.preventDefault()
	const id = dragId.value ?? e.dataTransfer?.getData('text/plain')
	dragId.value = null
	overKey.value = null
	const event = props.events.find((x) => String(x.id) === String(id))
	if (!event || dayKey(event.start) === key) return
	emit('move-event', { event, key })
}

const onDayClick = (day, e) => {
	if (e.target.closest('[data-event-chip]')) return
	emit('select-day', day.key)
}
</script>

<template>
	<div class="select-none rounded-lg bg-surface-2 p-2" data-testid="calendar-grid" :data-view="view">
		<div class="grid grid-cols-7 gap-px px-px pb-1 text-center text-[11px] font-semibold uppercase tracking-wider text-surface-6">
			<div v-for="(name, i) in WEEKDAYS" :key="name" :class="view === 'week' ? 'py-1' : ''">
				{{ name }}
				<span v-if="view === 'week'" class="ml-1 font-normal normal-case tracking-normal" :class="days[i].key === todayKey ? 'text-white' : 'text-surface-7'">{{ days[i].date.getDate() }}</span>
			</div>
		</div>
		<div class="grid grid-cols-7 gap-px overflow-hidden rounded-md bg-surface-4/60">
			<template v-for="(week, wi) in weeks" :key="wi">
				<div
					v-for="day in week"
					:key="day.key"
					class="relative flex flex-col gap-0.5 bg-surface-2 p-1 transition-colors"
					:class="[
						view === 'week' ? 'min-h-[22rem]' : 'min-h-[5.75rem]',
						day.inMonth ? 'cursor-pointer hover:bg-surface-3/70' : 'cursor-pointer text-surface-6 hover:bg-surface-3/40',
						overKey === day.key ? 'bg-main-transparent ring-1 ring-inset ring-main' : '',
					]"
					:data-testid="`calendar-day-${day.key}`"
					:data-in-month="day.inMonth ? 'true' : 'false'"
					role="gridcell"
					:aria-label="day.date.toLocaleDateString(undefined, { dateStyle: 'full' })"
					@click="onDayClick(day, $event)"
					@dragover="onDragOver(day.key, $event)"
					@dragleave="overKey === day.key && (overKey = null)"
					@drop="onDrop(day.key, $event)"
				>
					<div v-if="view === 'month'" class="flex items-center justify-between px-0.5">
						<span
							class="grid size-5 place-items-center rounded-full text-[11px] font-medium"
							:class="day.key === todayKey ? 'bg-main text-white' : day.inMonth ? 'text-surface-9' : 'text-surface-6'"
						>{{ day.date.getDate() }}</span>
						<span v-if="(eventsByDay[day.key] || []).length" class="text-[10px] text-surface-6">{{ (eventsByDay[day.key] || []).length }}</span>
					</div>
					<button
						v-for="event in visibleEvents(day.key)"
						:key="event.id"
						type="button"
						data-event-chip
						:class="chipClass(event)"
						:data-testid="`event-${event.id}`"
						:data-state="stateOf(event)"
						:title="`${chipTitle(event)} · ${fmtTime(event.start)}${event.end ? ` – ${fmtTime(event.end)}` : ''}`"
						draggable="true"
						@dragstart="onDragStart(event, $event)"
						@dragend="onDragEnd"
						@click.stop="emit('select-event', event)"
					>
						<span class="shrink-0 tabular-nums opacity-80" :class="view === 'month' ? 'hidden 2xl:inline' : ''">{{ fmtTime(event.start) }}</span>
						<span class="min-w-0 truncate font-medium">{{ chipTitle(event) }}</span>
						<span v-if="contentById[String(event.contentId)] && contentById[String(event.contentId)].published === false" class="ml-auto shrink-0 text-[9px] uppercase opacity-80">hidden</span>
					</button>
					<button
						v-if="hiddenCount(day.key) > 0"
						type="button"
						class="rounded px-1.5 py-0.5 text-left text-[11px] text-surface-7 hover:bg-surface-4 hover:text-white"
						@click.stop="expanded[day.key] = true"
					>
						+{{ hiddenCount(day.key) }} more
					</button>
				</div>
			</template>
		</div>
	</div>
</template>
